/* ────────────────────────────────────────────────────────────────
   Kushface — Intro gate / age gate
   Pairs with intro-gate.css and intro-gate.liquid (or _preview/index.html).

   Flow:
   1. Anti-FOUC head script has already decided whether to show the gate
      (html[data-intro="show" | "skip"]) before this file runs.
   2. If the gate is showing:
        a. Video plays once (no loop). Foreground UI is hidden.
        b. On video `ended` → set .intro-gate[data-stage="ready"] →
           CSS reveals the logo + age confirm + buttons with a staggered rise.
        c. Failsafes: no video / load error / autoplay blocked / reduced motion
           → reveal foreground immediately so the user is never trapped.
   3. ENTER (gated by 21+ checkbox) → fade out, sessionStorage flag set,
      gate removed from DOM.
   4. Under-21 decline → redirect (configurable via window.__kushfaceIntro).
   ──────────────────────────────────────────────────────────────── */

(function () {
  "use strict";

  var STORAGE_KEY = "kushface_intro_seen";
  var FAILSAFE_MS = 12000; // hard cap — reveal foreground after this no matter what
  var cfg = (window.__kushfaceIntro || {});
  var declineUrl = cfg.declineUrl || "https://www.google.com";

  var gate = document.querySelector(".intro-gate");
  if (!gate) return;

  var video = gate.querySelector(".intro-gate__video");
  var checkbox = gate.querySelector(".intro-gate__checkbox");
  var enterBtn = gate.querySelector(".intro-gate__enter");
  var declineBtn = gate.querySelector(".intro-gate__decline");
  var soundBtn = gate.querySelector(".intro-gate__sound");
  var skipBtn = gate.querySelector(".intro-gate__skip");
  var controls = gate.querySelector(".intro-gate__controls");

  // Hard-kill the video so audio can't bleed through. Removing the element
  // from the DOM isn't reliably enough in all browsers — also pause, mute,
  // strip every <source>, and call load() so the media pipeline tears down
  // cleanly before detachment.
  function killVideo() {
    if (!video) return;
    try {
      video.pause();
      video.muted = true;
      video.volume = 0;
      video.removeAttribute("autoplay");
      video.removeAttribute("src");
      var sources = video.querySelectorAll("source");
      sources.forEach(function (s) { s.removeAttribute("src"); });
      try { video.load(); } catch (e) {}
    } catch (e) {}
    if (video.parentNode) video.parentNode.removeChild(video);
  }

  // The gate is rendered on every homepage load (Liquid can't read the
  // client-side sessionStorage flag), then CSS-hidden once seen. On a refresh
  // after passing the gate, bail out before play()/the unmute-on-gesture trap
  // and kill the dormant video so its audio can't surface in the background.
  if (document.documentElement.getAttribute("data-intro") === "skip") {
    killVideo();
    if (gate.parentNode) gate.parentNode.removeChild(gate);
    return;
  }

  if (video) {
    video.defaultPlaybackRate = 1.2;
    video.playbackRate = 1.2;
  }

  var reducedMotion = window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var revealed = false;
  function reveal() {
    if (revealed) return;
    revealed = true;
    gate.setAttribute("data-stage", "ready");
  }

  // ── Decide whether to wait for video or reveal immediately ────
  var hasPlayableVideo = !!(video && (video.currentSrc ||
    (video.querySelector && video.querySelector("source"))));

  // Shared transition: video ends OR user skips → reveal content + loop in bg.
  var loopStarted = false;
  function startBackgroundLoop() {
    if (loopStarted || !video) return;
    loopStarted = true;
    try {
      video.loop = true;
      video.currentTime = 0;
      var lp = video.play();
      if (lp && typeof lp.catch === "function") lp.catch(function () {});
    } catch (e) {}
  }

  function finishIntro() {
    reveal();
    startBackgroundLoop();
  }

  if (reducedMotion || !hasPlayableVideo) {
    // No video phase. Show foreground straight away.
    reveal();
    // Skip is irrelevant when there was no video phase; remove just that button.
    // Sound stays — audio could still play once a real video is provided in prod.
    if (skipBtn && skipBtn.parentNode) skipBtn.parentNode.removeChild(skipBtn);
    if (!hasPlayableVideo && controls && controls.parentNode) {
      controls.parentNode.removeChild(controls);
    }
  } else {
    // Wait for the video to end (or for the user to skip), then transition.
    var markReady = function () {
      video.setAttribute("data-ready", "true");
      video.removeEventListener("loadeddata", markReady);
    };
    video.addEventListener("loadeddata", markReady);
    if (video.readyState >= 2) markReady();

    video.addEventListener("ended", finishIntro, { once: true });
    video.addEventListener("error", reveal, { once: true });

    // Best-effort play. If autoplay is blocked, reveal immediately so
    // the user is not stuck staring at a frozen poster.
    var playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === "function") {
      playAttempt.catch(reveal);
    }

    // Hard failsafe: never wait longer than FAILSAFE_MS to reveal.
    setTimeout(reveal, FAILSAFE_MS);
  }

  // ── Skip intro ───────────────────────────────────────────────
  if (skipBtn) {
    skipBtn.addEventListener("click", function () {
      finishIntro();
    });
  }

  // ── Sound toggle ──────────────────────────────────────────────
  if (soundBtn && video) {
    // Pulse the sound button (CSS keyframe on .intro-gate__sound--cue)
    // until the user acknowledges — either by clicking it directly or by
    // firing any other gesture that auto-unmutes. Cleared the same frame
    // audio comes on so the affordance and the payoff land together.
    soundBtn.classList.add("intro-gate__sound--cue");
    var stopCue = function () {
      soundBtn.classList.remove("intro-gate__sound--cue");
    };

    var applySound = function (muted) {
      try { video.muted = muted; } catch (e) {}
      soundBtn.setAttribute("data-muted", muted ? "true" : "false");
      soundBtn.setAttribute("aria-pressed", muted ? "false" : "true");
      soundBtn.setAttribute("aria-label", muted ? "Turn sound on" : "Turn sound off");
    };

    soundBtn.addEventListener("click", function () {
      stopCue();
      var nextMuted = !video.muted;
      applySound(nextMuted);
      if (!nextMuted) {
        // Unmuting: if the video already ended or is paused, replay from start
        // so the user actually hears it (otherwise we just toggled silence).
        if (video.ended) {
          try { video.currentTime = 0; } catch (e) {}
        }
        if (video.paused || video.ended) {
          var p = video.play();
          if (p && typeof p.catch === "function") p.catch(function () {});
        }
      }
    });

    // Audio default: unmute on the first real user gesture anywhere on the page.
    // Browsers require muted autoplay, so this is the closest we can get to
    // "audio on by default" — it comes on the moment the user engages.
    // Skip auto-unmute when the gesture lands on the sound button itself, so
    // the button's own click handler isn't fighting the document handler.
    // Once any gesture fires, all listeners (pointer/touch/key) retire together
    // so an intentional re-mute later doesn't get reversed by a stray keypress.
    var gestureEvents = ["pointerdown", "touchstart", "keydown"];
    var stopGestureListening = function () {
      gestureEvents.forEach(function (evt) {
        document.removeEventListener(evt, unmuteOnGesture);
      });
    };
    var unmuteOnGesture = function (e) {
      stopGestureListening();
      if (e && e.target && soundBtn.contains(e.target)) return;
      stopCue();
      if (video.muted) applySound(false);
    };
    gestureEvents.forEach(function (evt) {
      document.addEventListener(evt, unmuteOnGesture, { passive: true });
    });
  }

  // ── Enter enable/disable based on age checkbox ────────────────
  if (checkbox && enterBtn) {
    var sync = function () {
      enterBtn.disabled = !checkbox.checked;
    };
    sync();
    checkbox.addEventListener("change", sync);
  }

  // ── Dismiss on ENTER ──────────────────────────────────────────
  var dismiss = function () {
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch (e) {}
    // Stop the document-level "unmute on first gesture" handler before
    // anything else, so the ENTER click itself can't re-unmute the video.
    if (typeof stopGestureListening === "function") {
      try { stopGestureListening(); } catch (e) {}
    }
    killVideo();
    // The browser's auto scroll-restoration may have restored a pre-reload
    // scroll position while the fixed gate masked it. Reset to top while the
    // gate is still fully opaque — before the fade — so the content is never
    // revealed mid-scroll.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    gate.setAttribute("data-state", "dismissing");
    document.documentElement.setAttribute("data-intro", "skip");
    setTimeout(function () {
      if (gate.parentNode) gate.parentNode.removeChild(gate);
      document.body.style.overflow = "";
    }, 600);
  };

  if (enterBtn) {
    enterBtn.addEventListener("click", function (e) {
      if (enterBtn.disabled) return;
      e.preventDefault();
      dismiss();
    });
  }

  // ── Decline (Under 21) ───────────────────────────────────────
  if (declineBtn) {
    declineBtn.addEventListener("click", function (e) {
      e.preventDefault();
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
      window.location.replace(declineUrl);
    });
  }

  // ── Keyboard: Enter on checkbox submits if checked ────────────
  if (checkbox) {
    checkbox.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && checkbox.checked && enterBtn && !enterBtn.disabled) {
        e.preventDefault();
        dismiss();
      }
    });
  }
})();
